import { DebugBox } from "./DebugBox";
import { formatCurrency } from "../lib/formatters";

interface ExpenditureDebugProps {
  household: any;
  expenditure: any;
  background: any;
  householdExpenditure: any;
  totalCostsCalculations: any;
  onsMatrix: any;
}

export function ExpenditureDebug({ household, expenditure, background, householdExpenditure, totalCostsCalculations, onsMatrix }: ExpenditureDebugProps) {
  // Only show debug info after calculations have been performed
  if (!totalCostsCalculations || !totalCostsCalculations.totalCostsMonthly) {
    return null;
  }

  if (!householdExpenditure) {
    return (
      <DebugBox title="Expenditure Calculations" color="purple">
        <div className="bg-white dark:bg-gray-700 p-3 rounded border border-gray-200 dark:border-gray-600 text-purple-700 dark:text-purple-300">
          Loading expenditure calculations...
        </div>
      </DebugBox>
    );
  }

  return (
    <DebugBox title="Expenditure Calculations" color="purple">
      <p className="text-purple-700 dark:text-purple-300 text-sm mb-3">
        Real-time expenditure calculation variables based on current form inputs
      </p>
      
      <div className="space-y-4">
        {/* ONS Household Expenditure */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
          <h5 className="font-subheader text-purple-800 dark:text-purple-300 mb-3">ONS Household Expenditure</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900 dark:text-gray-100">
            <div>
              <strong>Adults:</strong><br />
              <span className="font-mono">{householdExpenditure.adults}</span>
            </div>
            <div>
              <strong>Children:</strong><br />
              <span className="font-mono">{householdExpenditure.children}</span>
            </div>
            <div>
              <strong>First Adult Cost:</strong><br />
              <span className="font-mono">{formatCurrency(householdExpenditure.firstAdultCost)}</span>
            </div>
            <div>
              <strong>Additional Adults Cost:</strong><br />
              <span className="font-mono">{formatCurrency(householdExpenditure.additionalAdultsCost)}</span>
            </div>
            <div>
              <strong>Children Cost:</strong><br />
              <span className="font-mono">{formatCurrency(householdExpenditure.childrenCost)}</span>
            </div>
            <div>
              <strong>Total Monthly:</strong><br />
              <span className="font-mono text-purple-700 dark:text-purple-300 font-subheader">{formatCurrency(householdExpenditure.totalMonthly)}</span>
            </div>
          </div>
          {householdExpenditure.calculationDetails && (
            <div className="mt-3 p-2 bg-purple-100 dark:bg-purple-900/30 rounded text-xs text-gray-900 dark:text-gray-100">
              <strong>Formula:</strong> <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{householdExpenditure.calculationDetails.formula}</code>
            </div>
          )}
        </div>

        {/* Total Costs Calculation */}
        <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
          <h5 className="font-subheader text-purple-800 dark:text-purple-300 mb-3">Total Monthly Costs</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900 dark:text-gray-100">
            <div>
              <strong>Effective Household Expenditure:</strong><br />
              <span className="font-mono">{formatCurrency(householdExpenditure?.totalMonthly || expenditure?.householdOnsOverride || 0)}</span>
            </div>
            <div>
              <strong>Credit Commitments:</strong><br />
              <span className="font-mono">{formatCurrency(expenditure?.commitments?.creditCommitments || 0)}</span>
            </div>
            <div>
              <strong>Additional Commitments:</strong><br />
              <span className="font-mono">{formatCurrency(expenditure?.commitments?.additionalCommitments || 0)}</span>
            </div>
            <div>
              <strong>Pension Contributions:</strong><br />
              <span className="font-mono">{formatCurrency(expenditure?.commitments?.pensionContributions || 0)}</span>
            </div>
            <div>
              <strong>Other Mortgages:</strong><br />
              <span className="font-mono">{formatCurrency(expenditure?.commitments?.otherMortgages || 0)}</span>
            </div>
            <div>
              <strong>BTL Mortgage (Stressed):</strong><br />
              <span className="font-mono">{formatCurrency(totalCostsCalculations.btlMortgageStressed || 0)}</span>
            </div>
            <div>
              <strong>BTL Running Costs:</strong><br />
              <span className="font-mono">{formatCurrency(totalCostsCalculations.btlRunningCosts || 0)}</span>
            </div>
            <div>
              <strong>Other Mortgages (Stressed):</strong><br />
              <span className="font-mono">{formatCurrency(totalCostsCalculations.otherMortgagesStressed || 0)}</span>
            </div>
            <div>
              <strong>Second Home HH Expenses:</strong><br />
              <span className="font-mono">{formatCurrency(totalCostsCalculations.secondHomeHhExpenses || 0)}</span>
            </div>
            <div>
              <strong>Total Monthly Costs:</strong><br />
              <span className="font-mono text-purple-700 dark:text-purple-300 font-subheader">{formatCurrency(totalCostsCalculations.totalCostsMonthly)}</span>
            </div>
          </div>
          {totalCostsCalculations.calculationDetails?.totalCostsMonthly && (
            <div className="mt-3 p-2 bg-purple-100 dark:bg-purple-900/30 rounded text-xs text-gray-900 dark:text-gray-100">
              <strong>Formula:</strong> <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{totalCostsCalculations.calculationDetails.totalCostsMonthly.formula}</code>
            </div>
          )}
        </div>

        {/* ONS Matrix Table - Detailed Breakdown */}
        {onsMatrix && Array.isArray(onsMatrix) && (
          <div className="bg-white dark:bg-gray-700 p-4 rounded border border-gray-200 dark:border-gray-600">
            <h5 className="font-subheader text-purple-800 dark:text-purple-300 mb-3">ONS Matrix - Detailed Breakdown</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-gray-900 dark:text-gray-100 border-collapse">
                <thead>
                  <tr className="border-b border-gray-300 dark:border-gray-600">
                    <th className="text-left py-2 px-2 font-subheader">Cost Category</th>
                    <th className="text-right py-2 px-2 font-subheader">First Adult</th>
                    <th className="text-right py-2 px-2 font-subheader">Additional Adult</th>
                    <th className="text-right py-2 px-2 font-subheader">Child</th>
                    <th className="text-right py-2 px-2 font-subheader bg-purple-50 dark:bg-purple-900/30">
                      {householdExpenditure.adults} Adult{householdExpenditure.adults !== 1 ? 's' : ''}, {householdExpenditure.children} Child{householdExpenditure.children !== 1 ? 'ren' : ''}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {onsMatrix.map((item: any, index: number) => {
                    const rowTotal = item.firstAdult + (item.additionalAdult * Math.max(0, householdExpenditure.adults - 1)) + (item.child * householdExpenditure.children);
                    return (
                    <tr key={index} className="border-b border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600">
                      <td className="py-2 px-2 text-left">{item.type}</td>
                      <td className="py-2 px-2 text-right font-mono">{formatCurrency(item.firstAdult)}</td>
                      <td className="py-2 px-2 text-right font-mono">{formatCurrency(item.additionalAdult)}</td>
                      <td className="py-2 px-2 text-right font-mono">{formatCurrency(item.child)}</td>
                      <td className="py-2 px-2 text-right font-mono font-subheader bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">{formatCurrency(rowTotal)}</td>
                    </tr>
                    );
                  })}
                  <tr className="border-t-2 border-purple-300 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/30 font-subheader">
                    <td className="py-2 px-2 text-left">TOTALS</td>
                    <td className="py-2 px-2 text-right font-mono text-purple-700 dark:text-purple-300">
                      {formatCurrency(onsMatrix.reduce((sum: number, item: any) => sum + item.firstAdult, 0))}
                    </td>
                    <td className="py-2 px-2 text-right font-mono text-purple-700 dark:text-purple-300">
                      {formatCurrency(onsMatrix.reduce((sum: number, item: any) => sum + item.additionalAdult, 0))}
                    </td>
                    <td className="py-2 px-2 text-right font-mono text-purple-700 dark:text-purple-300">
                      {formatCurrency(onsMatrix.reduce((sum: number, item: any) => sum + item.child, 0))}
                    </td>
                    <td className="py-2 px-2 text-right font-mono text-purple-700 dark:text-purple-300 font-bold text-base">
                      {formatCurrency(householdExpenditure.totalMonthly)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-3 p-2 bg-purple-100 dark:bg-purple-900/30 rounded text-xs text-gray-900 dark:text-gray-100">
              <strong>Calculation:</strong> For {householdExpenditure.adults} adult(s) and {householdExpenditure.children} child(ren):
              <br />
              <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">
                First Adult: {formatCurrency(householdExpenditure.firstAdultCost)} + 
                Additional Adults ({householdExpenditure.adults - 1}): {formatCurrency(householdExpenditure.additionalAdultsCost)} + 
                Children ({householdExpenditure.children}): {formatCurrency(householdExpenditure.childrenCost)} = 
                {formatCurrency(householdExpenditure.totalMonthly)}
              </code>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded">
          <h5 className="font-subheader text-purple-800 dark:text-purple-300 mb-2">Expenditure Summary</h5>
          <div className="text-sm space-y-1 text-gray-900 dark:text-gray-100">
            <div><strong>ONS Household Expenditure:</strong> {formatCurrency(householdExpenditure.totalMonthly)}</div>
            <div><strong>Total Monthly Costs:</strong> {formatCurrency(totalCostsCalculations.totalCostsMonthly)}</div>
          </div>
        </div>
      </div>
    </DebugBox>
  );
}
